// src/api/client/axios.client.ts
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getStoredTokens, setStoredTokens, clearStoredTokens } from 'utils/token';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.gallerybaz.com/api';//'http://ocelot:80/api'; 

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

let isRefreshing = false;
let failedQueue: Array<{ resolve: (v?: unknown) => void; reject: (r?: unknown) => void }> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((p) => error ? p.reject(error) : p.resolve(token));
  failedQueue = [];
};

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = getStoredTokens();
    if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
    config.headers['Accept-Language'] = localStorage.getItem('locale') || 'fa';
    config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      const { refreshToken } = getStoredTokens();

      if (!refreshToken) {
        clearStoredTokens();
        //window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
        const { accessToken: newAT, refreshToken: newRT } = data.data;
        setStoredTokens(newAT, newRT);
        processQueue(null, newAT);
        originalRequest.headers.Authorization = `Bearer ${newAT}`;
        return apiClient(originalRequest);
      } catch (err) {
        processQueue(err as AxiosError, null);
        clearStoredTokens();
        window.location.href = '/login';
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
